import React from "react";
import { connect } from "react-redux";
import CommonReport from "../../../../reports/components/CommonReport";
import Toolbar, { Item } from "devextreme-react/toolbar";
import { Button } from "devextreme-react/button";
import { SelectBox } from "devextreme-react/select-box";
import { NumberBox } from "devextreme-react/number-box";
import { TextBox } from "devextreme-react/text-box";
import TextArea from "devextreme-react/text-area";
import DateBox from "devextreme-react/date-box";
import { Lookup, DropDownOptions } from "devextreme-react/lookup";
import { createStore } from "devextreme-aspnet-data-nojquery";
import DataSource from "devextreme/data/data_source";
import Accordion from "devextreme-react/accordion";
import { LoadPanel } from "devextreme-react";
import { TabPanel, Item as TabItem } from "devextreme-react/tab-panel";
import Popup from "devextreme-react/popup";
import DataGrid, { Column, Export, Sorting, FilterRow, Scrolling, Paging, ColumnChooser } from "devextreme-react/data-grid";
import { alert } from "devextreme/ui/dialog";
import axios from "axios";
import routes from "../../../../assets/apiRoutes.json";
import { MESSAGES } from "../../../../common/Messages";

// WM -> Transactions -> Charges -> Generate Storage Charges (Delphi WMD0015).
//
// Distinct from wm/components/charges/generateStorageCharges/GenerateStorageCharges.js, which is the
// screen of the same name under the top-level Charges group. That one previews through
// SPWM_RECURRINGSTORAGE_PREVIEW; this one runs SPWM_RECURRINGSTORAGE, whose Test mode returns the
// rows a live run would produce. Different route, own permission, so the two do not shadow each
// other.

const searchpanel = [
    {
        ID: 1,
        Name: "Search Filters"
    }
];

// Coded values the procedure takes. Delphi passes the first character of each combo item, so the
// codes are the initials of its lists - mirrored from Argos.Core RecurringStorageChargesConstants.
const MODE = { LIVE: "L", TEST: "T" };

const MODE_ITEMS = [
    { id: MODE.LIVE, name: "Generate Storage" },
    { id: MODE.TEST, name: "Test Storage Generation" },
];

const FREQUENCY_ITEMS = [
    { id: "W", name: "Weekly" },
    { id: "B", name: "Bi-Weekly" },
    { id: "M", name: "Monthly" },
    { id: "S", name: "Semi-Monthly" },
    { id: "A", name: "Anniversary Date" },
];

// "Ready To Invoice" is stored as 'L' - the ready-to-commit code - not as 'R'.
const STATUS_ITEMS = [
    { id: "O", name: "Open" },
    { id: "H", name: "Hold" },
    { id: "L", name: "Ready To Invoice" },
];

const EFFECTIVE_DATE_ITEMS = [
    { id: "B", name: "Use Begin As/Of Date as Effective Date" },
    { id: "T", name: "Use Transaction Date as Effective Date" },
];

const YES_NO_ITEMS = [
    { id: "Y", name: "Yes" },
    { id: "N", name: "No" },
];

// Anniversary day is a day of the month; the service widens anything outside this to 31.
const ANV_DAY_MIN = 1;
const ANV_DAY_MAX = 31;

let accessToken = "";

class GenerateStorageCharges extends React.Component {
    constructor(props) {
        super(props);
        accessToken = props.oidc.user.access_token;

        // Reaches CommonReport's getRangeFilters at submit. common/reportFilters.js explains why the
        // rows from onReportStateChange are the wrong source: a cell the user is still editing when
        // they press Start has not been written back to the row yet, so the posted filter silently
        // lacks it - and on a live run that generates charges nobody asked for. getRangeFilters awaits
        // commitPendingGridEdits first, and also maps flag descriptions back to codes and drops
        // value-less rows, neither of which reading from state does.
        //
        // The shared useCommittedFilterRows hook cannot be used here - this is a class component - so
        // the same ref-with-fallback shape is reproduced in getCommittedFilterRows below.
        this.reportRef = React.createRef();

        this.startButtonClicked = this.startButtonClicked.bind(this);
        this.viewTestResultsButtonClicked = this.viewTestResultsButtonClicked.bind(this);
        this.resetButtonClicked = this.resetButtonClicked.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.handleReportStateChange = this.handleReportStateChange.bind(this);
        this.selectionChanged = this.selectionChanged.bind(this);

        // FormAbLoadProgram defaults: both dates today, Weekly, Ready To Invoice, effective date
        // taken from the Begin date, not consolidated, and Generate (not Test).
        this.initialState = {
            previewData: null,
            isTestResultsPopupVisible: false,
            loadIndicatorVisible: false,
            animationDuration: 300,
            selectedItems: searchpanel,
            report: {
                mode: MODE.LIVE,
                beginDate: new Date(),
                transDate: new Date(),
                reference: "",
                frequency: "W",
                status: "L",
                effectiveDateSource: "B",
                comment: "",
                anvDayThru: null,
                consolidateByDepartment: "N",
                rDepartment: null,
                filters: [],
                filterWhereInfoList: []
            }
        };
        this.state = { ...this.initialState };

        this.departmentDataSource = new DataSource({
            store: new createStore({
                key: "id",
                loadUrl: `${window._env_.REACT_APP_APIS}/ac/Lookup/GetDepartments`,
                onBeforeSend: function (method, ajaxOptions) {
                    ajaxOptions.headers = {
                        access_token: accessToken,
                        Authorization: `Bearer ${accessToken}`
                    };
                }
            }),
            paginate: true,
            pageSize: 100
        });
    }

    // CommonReport owns the filter drawer and hands back the whole report object. Merge rather than
    // replace, or the screen's own fields are wiped every time the filters change.
    handleReportStateChange = (reportState) => {
        this.setState((prevState) => ({
            report: { ...prevState.report, ...reportState }
        }));
    }

    handleChange = (field, value) => {
        this.setState((prevState) => ({
            report: { ...prevState.report, [field]: value }
        }));
    }

    selectionChanged = (e) => {
        let newItems = [...this.state.selectedItems];
        e.removedItems.forEach((item) => {
            const index = newItems.indexOf(item);
            if (index >= 0) {
                newItems.splice(index, 1);
            }
        });
        if (e.addedItems.length) {
            newItems = [...newItems, ...e.addedItems];
        }
        this.setState({ selectedItems: newItems });
    }

    // The btnStartClick refusals, raised before the round trip. The service repeats them, so a direct
    // API call is refused too.
    validate = (report) => {
        if (!report.transDate) return MESSAGES.STORAGE_CHARGES_TRANSACTION_DATE_REQUIRED;
        if (!report.beginDate) return MESSAGES.STORAGE_CHARGES_BEGIN_DATE_REQUIRED;
        if (!report.frequency) return MESSAGES.STORAGE_CHARGES_FREQUENCY_REQUIRED;
        if (!report.status) return MESSAGES.STORAGE_CHARGES_STATUS_REQUIRED;
        if (!report.effectiveDateSource) return MESSAGES.STORAGE_CHARGES_EFFECTIVE_DATE_REQUIRED;
        if (report.consolidateByDepartment === "Y" && !report.rDepartment) return MESSAGES.STORAGE_CHARGES_DEPARTMENT_REQUIRED;
        return null;
    }

    // Mirrors useCommittedFilterRows: the ref when it is wired, the rows from state only as a
    // mount-race fallback so it degrades rather than throwing.
    getCommittedFilterRows = async () => {
        const report = this.reportRef?.current;

        if (report && typeof report.getRangeFilters === "function") {
            return await report.getRangeFilters();
        }

        return this.state.report.filterWhereInfoList || [];
    }

    startButtonClicked = async (e) => {
        if (e?.event) e.event.stopPropagation();

        const report = this.state.report;
        const refusal = this.validate(report);
        if (refusal) {
            alert(refusal, MESSAGES.WARNING);
            return;
        }

        this.setState({ loadIndicatorVisible: true });
        try {
            const filters = await this.getCommittedFilterRows();

            const res = await axios.post(
                `${window._env_.REACT_APP_APIS}/${routes.Transaction.GenerateRecurringStorageCharges}`,
                JSON.stringify({ ...report, filters: filters }),
                {
                    headers: {
                        "Content-Type": "application/json",
                        access_token: accessToken,
                        Authorization: `Bearer ${accessToken}`,
                    },
                }
            );

            const result = res.data;
            if (!result.Success) {
                alert(result.Message || MESSAGES.STORAGE_CHARGES_GENERATION_FAILED, MESSAGES.ERROR);
                return;
            }

            if (report.mode === MODE.TEST) {
                // Nothing was written; show what a live run would have produced.
                this.setState({
                    previewData: result.Preview || [],
                    isTestResultsPopupVisible: true
                });
            }
            alert(result.Message, MESSAGES.INFORMATION);
        } catch (error) {
            console.error("Error generating storage charges:", error);
            alert(error?.response?.data || error.message || MESSAGES.STORAGE_CHARGES_GENERATION_FAILED, MESSAGES.ERROR);
        } finally {
            this.setState({ loadIndicatorVisible: false });
        }
    }

    // Reopens the results of the last test run. A live run writes charges rather than returning
    // rows, so there is nothing to show until a test has been run.
    viewTestResultsButtonClicked = (e) => {
        if (e?.event) e.event.stopPropagation();
        if (!this.state.previewData) {
            alert(MESSAGES.STORAGE_CHARGES_NO_TEST_RESULTS, MESSAGES.INFORMATION);
            return;
        }
        this.setState({ isTestResultsPopupVisible: true });
    }

    resetButtonClicked = (e) => {
        if (e?.event) e.event.stopPropagation();
        this.setState({ ...this.initialState, report: { ...this.initialState.report, beginDate: new Date(), transDate: new Date() } });
    }

    render() {
        const report = this.state.report;
        const consolidating = report.consolidateByDepartment === "Y";

        return (
            <>
                <LoadPanel
                    shadingColor="rgba(0,0,0,0.4)"
                    onHiding={(() => this.setState({ loadIndicatorVisible: false }))}
                    visible={this.state.loadIndicatorVisible}
                    showIndicator={true}
                    shading={true}
                    showPane={true}
                    hideOnOutsideClick={false}
                />
                <div className="row">
                    <div className="col-md-12 main-page">
                        <div id="accordion">
                            <Accordion
                                dataSource={searchpanel}
                                collapsible={true}
                                multiple={false}
                                animationDuration={this.state.animationDuration}
                                selectedItems={this.state.selectedItems}
                                onSelectionChanged={this.selectionChanged}
                                itemTitleRender={() => (
                                    <Toolbar className="accordian-toolbar">
                                        <Item location="before">
                                            <div className="title">
                                                <h6 style={{ color: window._env_.Color2 }}>
                                                    Generate Storage Charges
                                                </h6>
                                            </div>
                                        </Item>
                                        <Item location="after">
                                            <Button className="accordianButton" text="Start" onClick={this.startButtonClicked} type="default" />
                                        </Item>
                                        <Item location="after">
                                            <Button className="accordianButton" text="View Test Results" onClick={this.viewTestResultsButtonClicked} type="default" />
                                        </Item>
                                        <Item location="after">
                                            <Button className="accordianButton" text="Reset" onClick={this.resetButtonClicked} />
                                        </Item>
                                    </Toolbar>
                                )}
                                itemRender={() => (
                                    <div>
                                        <TabPanel height="auto">
                                            <TabItem title="Calculate Storage">
                                                <div className="row" style={{ padding: "10px" }}>
                                                    <div className="col-md-3">
                                                        <SelectBox value={report.mode} dataSource={MODE_ITEMS} valueExpr="id" displayExpr="name" stylingMode="outlined" labelMode="static" label="Mode" onValueChanged={(e) => this.handleChange("mode", e.value)} />
                                                    </div>
                                                    <div className="col-md-3">
                                                        <DateBox value={report.transDate} type="date" stylingMode="outlined" labelMode="static" label="Trans Date" onValueChanged={(e) => this.handleChange("transDate", e.value)} />
                                                    </div>
                                                    <div className="col-md-3">
                                                        <DateBox value={report.beginDate} type="date" stylingMode="outlined" labelMode="static" label="Begin/As Of" onValueChanged={(e) => this.handleChange("beginDate", e.value)} />
                                                    </div>
                                                    <div className="col-md-3">
                                                        <TextBox value={report.reference} stylingMode="outlined" labelMode="static" label="Reference" onValueChanged={(e) => this.handleChange("reference", e.value)} />
                                                    </div>
                                                </div>
                                                <div className="row" style={{ padding: "0px 10px 10px 10px" }}>
                                                    <div className="col-md-3">
                                                        <SelectBox value={report.frequency} dataSource={FREQUENCY_ITEMS} valueExpr="id" displayExpr="name" stylingMode="outlined" labelMode="static" label="Frequency" onValueChanged={(e) => this.handleChange("frequency", e.value)} />
                                                    </div>
                                                    <div className="col-md-3">
                                                        <SelectBox value={report.status} dataSource={STATUS_ITEMS} valueExpr="id" displayExpr="name" stylingMode="outlined" labelMode="static" label="Status" onValueChanged={(e) => this.handleChange("status", e.value)} />
                                                    </div>
                                                    <div className="col-md-6">
                                                        <SelectBox value={report.effectiveDateSource} dataSource={EFFECTIVE_DATE_ITEMS} valueExpr="id" displayExpr="name" stylingMode="outlined" labelMode="static" label="Effective Date" onValueChanged={(e) => this.handleChange("effectiveDateSource", e.value)} />
                                                    </div>
                                                </div>
                                                <div className="row" style={{ padding: "0px 10px 0px 10px" }}>
                                                    <div className="col-md-3">
                                                        <div className="title"><h6 style={{ color: window._env_.Color2 }}>Anniversary Date Only</h6></div>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <div className="title"><h6 style={{ color: window._env_.Color2 }}>Consolidate by Department</h6></div>
                                                    </div>
                                                </div>
                                                <div className="row" style={{ padding: "0px 10px 10px 10px" }}>
                                                    <div className="col-md-3">
                                                        <NumberBox value={report.anvDayThru} min={ANV_DAY_MIN} max={ANV_DAY_MAX} showSpinButtons={true} stylingMode="outlined" labelMode="static" label="Anv Day Thru" onValueChanged={(e) => this.handleChange("anvDayThru", e.value)} />
                                                    </div>
                                                    <div className="col-md-3">
                                                        <SelectBox value={report.consolidateByDepartment} dataSource={YES_NO_ITEMS} valueExpr="id" displayExpr="name" stylingMode="outlined" labelMode="static" label="Consolidate"
                                                            onValueChanged={(e) => {
                                                                // Delphi clears the department as it disables it, so a stale
                                                                // choice is not sent once consolidation is turned back off.
                                                                this.setState((prevState) => ({
                                                                    report: {
                                                                        ...prevState.report,
                                                                        consolidateByDepartment: e.value,
                                                                        rDepartment: e.value === "Y" ? prevState.report.rDepartment : null
                                                                    }
                                                                }));
                                                            }} />
                                                    </div>
                                                    <div className="col-md-3">
                                                        <Lookup dataSource={this.departmentDataSource} value={report.rDepartment} valueExpr="id" displayExpr="name" searchEnabled={true} showClearButton={true} disabled={!consolidating} stylingMode="outlined" labelMode="static" label="Department" onValueChanged={(e) => this.handleChange("rDepartment", e.value)}>
                                                            <DropDownOptions showTitle={false} />
                                                        </Lookup>
                                                    </div>
                                                </div>
                                                <div className="row" style={{ padding: "0px 10px 10px 10px" }}>
                                                    <div className="col-md-9">
                                                        <TextArea value={report.comment} stylingMode="outlined" labelMode="static" label="Comment" autoResizeEnabled={false} onValueChanged={(e) => this.handleChange("comment", e.value)} />
                                                    </div>
                                                </div>
                                            </TabItem>
                                        </TabPanel>
                                    </div>
                                )}
                                id="accordion-container" className="accordion-height"
                            />
                        </div>
                    </div>
                </div>
                {this.state.isTestResultsPopupVisible &&
                    <Popup
                        visible={this.state.isTestResultsPopupVisible}
                        onHiding={() => this.setState({ isTestResultsPopupVisible: false })}
                        dragEnabled={true}
                        hideOnOutsideClick={true}
                        showCloseButton={true}
                        showTitle={true}
                        title="View Test Results"
                        container=".dx-viewport"
                        width="90%"
                        height="80%"
                    >
                            <DataGrid
                                dataSource={this.state.previewData}
                                showBorders={true}
                                rowAlternationEnabled={true}
                                allowColumnReordering={true}
                                allowColumnResizing={true}
                                columnAutoWidth={true}
                                height="100%"
                                onExporting={(e) => {
                                    if (window.handleDevExtremeDataGridExport) {
                                        window.handleDevExtremeDataGridExport(e);
                                    }
                                }}
                            >
                                <Sorting mode="multiple" />
                                <FilterRow visible={true} />
                                <Scrolling mode="virtual" rowRenderingMode="virtual" />
                                <Paging enabled={true} pageSize={50} />
                                <Export enabled={true} />
                                <ColumnChooser enabled={true} />
                                <Column dataField="BillToCustomer" caption="Bill To Customer" />
                                <Column dataField="ItemCode" caption="Item Code" />
                                <Column dataField="LotCode" caption="Lot Code" />
                                <Column dataField="ChargeCode" caption="Charge Code" />
                                <Column dataField="QuantityBillAs" caption="Bill As Qty" dataType="number" alignment="right" />
                                <Column dataField="Rate" caption="Rate" dataType="number" alignment="right" />
                                <Column dataField="Amount" caption="Amount" dataType="number" alignment="right" format={{ type: "fixedPoint", precision: 2 }} />
                                <Column dataField="LastBilledDate" caption="Date Billed" dataType="date" />
                                <Column dataField="NextBilledDate" caption="Next Bill" dataType="date" />
                            </DataGrid>
                    </Popup>
                }
                <CommonReport
                    innerRef={this.reportRef}
                    name="WMRecurringStorageCharges"
                    fullName="Argos.Report.WM.WMRecurringStorageCharges, Argos.Report.WM"
                    state={this.state}
                    onReportStateChange={this.handleReportStateChange}
                />
            </>
        );
    }
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(GenerateStorageCharges);
